import { useLayoutEffect, useMemo, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"
import axios from "axios"
import moment from "moment"

import { Menu } from "@headlessui/react"

import { usePage } from "contexts/page-context"
import { Pagination, Transition } from "components"
import Input from "components/forms/input"
import Label from "components/forms/label"
import SelectDescription from "components/forms/select-description"
import useBranchRepositories from "repositories/branch"
import { useResultSet } from "repositories/db-repository"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { Pencil } from "components/icons/outline"

const BASE = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")

const STATUS_OPTIONS = [
  { id: "ALL", name: "Semua Status" },
  { id: "SUBMITTED", name: "Submitted" },
  { id: "APPROVED", name: "Approved" },
  { id: "DONE", name: "Selesai" },
  { id: "PAID", name: "Paid" },
  { id: "REJECTED", name: "Ditolak" },
  { id: "CANCELED", name: "Dibatalkan" },
]

const formatCurrency = (value) =>
  Number(value ?? 0).toLocaleString("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 2 })

// 210726: qty (mis. LUSIN pecahan) maksimal 2 desimal — 1,92 bukan 1,9166…
const formatQty = (value) =>
  Number(value ?? 0).toLocaleString("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const statusBadgeClass = (status) => {
  const s = String(status || "").toUpperCase()
  if (s === "PAID" || s === "DONE") return "bg-green-50 text-green-700 border-green-200"
  if (s === "APPROVED") return "bg-blue-50 text-blue-700 border-blue-200"
  if (s === "SUBMITTED") return "bg-amber-50 text-amber-700 border-amber-200"
  if (s === "CANCELED" || s === "VOID" || s === "REJECTED") return "bg-rose-50 text-rose-700 border-rose-200"
  return "bg-neutral-50 text-neutral-700 border-neutral-200"
}

// 280726 (revisi permintaan user): tombol-tombol print (DO / Surat Jalan / Faktur)
// DILEBUR jadi SATU tombol "Print" ber-dropdown — meniru tombol "Export" di list
// Pembelian (PurchaseExportActions, varian compact). Edit jadi ikon pensil bulat.
const PRINT_ITEMS = [
  { label: "DO", description: "Dokumen picking", path: "/print/delivery-order" },
  { label: "Surat Jalan", description: "Nomor SJ", path: "/print/surat-jalan" },
  { label: "Faktur", description: "Bentuk faktur PO", path: "/print/faktur" },
]

function PrintActions({ orderId, onOpen }) {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="inline-flex items-center gap-2 border border-neutral-200 bg-white text-neutral-800 shadow-sm transition hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-200 px-3 py-2 text-[11px] rounded-lg">
        <span>Print</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-3.5 h-3.5 text-neutral-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </Menu.Button>

      <Menu.Items className="absolute right-0 z-30 mt-2 w-56 origin-top-right overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl ring-1 ring-black/5 focus:outline-none">
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 bg-neutral-50">
          Order HO
        </div>
        <div className="p-1">
          {PRINT_ITEMS.map((item) => (
            <Menu.Item key={item.path}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onOpen(orderId, item.path)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition ${
                    active ? "bg-neutral-100" : "bg-white"
                  }`}
                >
                  <span className="text-xs font-semibold text-neutral-800">{item.label}</span>
                  <span className="text-[10px] text-neutral-500">{item.description}</span>
                </button>
              )}
            </Menu.Item>
          ))}
        </div>
      </Menu.Items>
    </Menu>
  )
}

// 270726 (permintaan user): gaya tombol aksi DISERAGAMKAN dengan menu Pembelian
// (pages/purchase/list.jsx) — px-3 py-2, teks 11px semibold, rounded-lg, hover ke
// shade lebih terang, disabled opacity-60. Warna tetap beda per aksi.
// Kelas ditulis UTUH (bukan dirangkai `bg-${warna}-600`) karena Tailwind memindai
// source secara literal — kelas hasil rangkaian runtime tidak ikut ter-generate.
// (280726: konstanta DO/SJ/Faktur/Edit dihapus — print pindah ke dropdown
// PrintActions, Edit jadi ikon pensil.)
const BTN_BASE = "px-3 py-2 text-[11px] font-semibold rounded-lg disabled:opacity-60"
const BTN_GREEN = `${BTN_BASE} text-white bg-green-600 hover:bg-green-500`
const BTN_BLUE = `${BTN_BASE} text-white bg-blue-600 hover:bg-blue-500`
const BTN_ROSE = `${BTN_BASE} text-white bg-rose-600 hover:bg-rose-500`
const BTN_OUTLINE = `${BTN_BASE} text-neutral-700 border border-neutral-200 hover:bg-neutral-50`
const BTN_OUTLINE_ROSE = `${BTN_BASE} text-rose-700 border border-rose-300 hover:bg-rose-50`
const BTN_OUTLINE_DANGER = `${BTN_BASE} text-red-600 border border-red-200 hover:bg-red-50`

export default function List() {
  const { setTransition } = usePage()
  const { setTitle } = useOutletContext()
  const location = useLocation()

  useLayoutEffect(() => {
    setTransition(location.state?.transition)
    setTitle("Order HO ke Customer")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [searchParams, setSearchParams] = useSearchParams()
  const [searchText, setSearchText] = useState(searchParams.get("search") || "")
  const [cancelingId, setCancelingId] = useState(null)
  const [detailOrder, setDetailOrder] = useState(null) // hasil GET /orders/{id}
  const [detailLoadingId, setDetailLoadingId] = useState(null)

  const status = searchParams.get("status") || "ALL"
  // 240726: default filter "ikut outlet user" (grup HO/Semua Outlet).
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  // Grup → CSV id (mis. "1,15"); outlet individual → id tunggal.
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const startDate = searchParams.get("start_date") || ""
  const endDate = searchParams.get("end_date") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"

  const { data: branchSelection } = useBranchRepositories({ paginate: false })
  const branchNameById = useMemo(() => {
    const map = new Map()
    const branches = Array.isArray(branchSelection) ? branchSelection : []
    for (const b of branches) map.set(String(b.id), b.name)
    return map
  }, [branchSelection])

  const listParams = useMemo(
    () => ({
      search: searchParams.get("search") || undefined,
      status: status === "ALL" ? undefined : status,
      outlet_id: resolvedOutletCsv || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      page,
      per_page: perPage,
    }),
    [searchParams, status, resolvedOutletCsv, startDate, endDate, page, perPage]
  )

  const { data: listResponse, isLoading, mutate } = useResultSet(BASE, "orders/ho-orders", listParams)
  const paginator = listResponse?.data ?? listResponse ?? {}
  const rows = useMemo(() => (Array.isArray(paginator?.data) ? paginator.data : []), [paginator])

  const paginationLinks = useMemo(() => {
    const links = paginator?.links ?? []
    return links.map((link) => {
      if (!link?.url) return link
      const nextParams = new URLSearchParams(searchParams)
      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }
      return { ...link, url: `?${nextParams.toString()}` }
    })
  }, [paginator, searchParams])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }
    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
        delete merged[key]
      }
    })
    setSearchParams(merged)
  }

  const submitSearch = () => mergeParams({ search: searchText || null, page: 1 })

  const openPrint = (orderId, path) => {
    window.open(`${BASE}/orders/${orderId}${path}`, "_blank")
  }

  const openDetail = async (row) => {
    if (detailLoadingId) return
    try {
      setDetailLoadingId(row.id)
      const res = await axios.get(`${BASE}/orders/${row.id}`)
      setDetailOrder(res.data)
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal memuat detail order")
    } finally {
      setDetailLoadingId(null)
    }
  }

  // ATALLA-474: Batalkan order HO → backend mengembalikan stok (current + actual)
  // lewat jalur restore yang sama dengan cancel POS dan set status CANCELED
  // (otomatis keluar dari EOD & laporan penjualan).
  const onCancel = async (row) => {
    if (cancelingId) return
    const reason = window.prompt(
      `Batalkan order ${row.id}?\nCustomer: ${row.customer || "-"}\nTotal: Rp ${formatCurrency(row.nett_amount)}\n\nStok yang sudah terpotong akan dikembalikan.\nAlasan pembatalan (wajib):`,
      ""
    )
    if (reason === null) return
    if (!reason.trim()) {
      toast.error("Alasan pembatalan wajib diisi")
      return
    }

    try {
      setCancelingId(row.id)
      const res = await axios.post(`${BASE}/orders/${row.id}/cancel-ho`, { reason: reason.trim() })
      toast.success(res.data?.message || "Order dibatalkan & stok dikembalikan")
      await mutate()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal membatalkan order")
    } finally {
      setCancelingId(null)
    }
  }

  // 270726 (permintaan user): HAPUS order HO — order keluar dari daftar (bukan cuma
  // berstatus CANCELED seperti Batalkan). Order yang belum dibatalkan stoknya
  // dikembalikan dulu oleh backend, jadi tidak ada stok yang tertinggal terpotong.
  const [deletingId, setDeletingId] = useState(null)
  const onDelete = async (row) => {
    if (deletingId) return

    const isCanceled = ["CANCELED", "VOID", "REJECTED"].includes(
      String(row.payment_status || "").toUpperCase()
    )
    const confirmed = window.confirm(
      `Hapus order ${row.id}?\n` +
        `Customer: ${row.customer || "-"}\n` +
        `Total: Rp ${formatCurrency(row.nett_amount)}\n\n` +
        (isCanceled
          ? "Order sudah dibatalkan — stok sudah kembali.\n"
          : "Stok yang sudah terpotong akan dikembalikan lebih dulu.\n") +
        "Order akan hilang dari daftar dan tidak bisa dikembalikan."
    )
    if (!confirmed) return

    try {
      setDeletingId(row.id)
      const res = await axios.delete(`${BASE}/orders/${row.id}/ho`)
      toast.success(res.data?.message || "Order HO dihapus")
      await mutate()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal menghapus order")
    } finally {
      setDeletingId(null)
    }
  }

  // 170726 (flow order HO): SUBMITTED -> Approve/Reject; APPROVED -> Posting (selesai).
  const [actingId, setActingId] = useState(null)
  const doAction = async (row, path, confirmMsg, body = {}) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    try {
      setActingId(row.id)
      const res = await axios.post(`${BASE}/orders/${row.id}${path}`, body)
      toast.success(res?.data?.message || "Berhasil")
      await mutate()
    } catch (e) {
      toast.error(e?.response?.data?.message || "Gagal memproses order")
    } finally {
      setActingId(null)
    }
  }
  const onApprove = (row) =>
    doAction(row, "/approve-ho", `Approve order ${row.id}?\nStok fisik (actual) akan dipotong.`)
  const onReject = (row) => {
    const reason = window.prompt(`Reject order ${row.id}?\nStok reservasi akan dikembalikan.\nAlasan (wajib):`, "")
    if (reason === null) return
    if (!reason.trim()) { toast.error("Alasan wajib diisi"); return }
    doAction(row, "/reject-ho", null, { reason: reason.trim() })
  }
  const onPosting = (row) =>
    doAction(row, "/post-ho", `Posting order ${row.id}?\nOrder akan SELESAI dan terhitung di laporan penjualan.`)

  return (
    <Transition type="fade">
      <div className="space-y-6">
        <div className="p-5 bg-white border rounded-2xl border-neutral-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Daftar Order HO ke Customer</h2>
              <p className="mt-1 text-sm text-neutral-500">
                Riwayat penjualan langsung dari HO. Cetak DO/Faktur atau batalkan order (stok dikembalikan otomatis).
              </p>
            </div>
            <Link
              to="/ho-order/create"
              state={{ transition: "fade" }}
              className="px-4 py-2.5 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-600"
            >
              + Buat Order
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-6">
            <div className="md:col-span-2">
              <Label value="Cari" />
              <Input
                type="text"
                value={searchText}
                placeholder="No. order / nama customer..."
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
              />
            </div>
            <div>
              <Label value="Outlet" />
              <OutletFilterSelect
                value={outletId}
                onChange={(v) => mergeParams({ outlet_id: v, page: 1 })}
                className="w-full mt-1 p-2 text-sm transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
              />
            </div>
            <div>
              <Label value="Status" />
              <SelectDescription
                selection={STATUS_OPTIONS}
                value={status}
                onChange={(value) => mergeParams({ status: value || "ALL", page: 1 })}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                description={() => ""}
                placeholder="Status..."
              />
            </div>
            <div>
              <Label value="Tanggal Dari" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => mergeParams({ start_date: e.target.value, page: 1 })}
              />
            </div>
            <div>
              <Label value="Tanggal Sampai" />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => mergeParams({ end_date: e.target.value, page: 1 })}
              />
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-2xl border-neutral-200">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">No. Order</th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Tanggal</th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Customer</th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Outlet</th>
                  <th className="px-4 py-3 font-medium text-right text-neutral-500 whitespace-nowrap text-xs uppercase">Total (Nett)</th>
                  <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Status</th>
                  <th className="px-4 py-3 font-medium text-right text-neutral-500 whitespace-nowrap text-xs uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-neutral-500">Memuat data...</td>
                  </tr>
                )}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-neutral-500">
                      Belum ada order HO untuk filter yang dipilih.
                    </td>
                  </tr>
                )}
                {!isLoading && rows.map((row) => {
                  const rowStatus = String(row.payment_status || "").toUpperCase()
                  const isCanceled = rowStatus === "CANCELED" || rowStatus === "VOID"
                  const isCanceling = cancelingId === row.id

                  return (
                    <tr key={row.id} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-neutral-900 whitespace-nowrap">
                        <div className="break-all">{row.order_number || row.id}</div>
                        <div className="text-[11px] text-neutral-500">oleh {row.created_by || "-"}</div>
                      </td>
                      <td className="px-4 py-3 text-neutral-800 whitespace-nowrap">
                        {row.order_date ? moment(row.order_date).format("DD/MM/YYYY") : "-"}
                        <div className="text-[11px] text-neutral-500">
                          {row.created_at ? moment(row.created_at).format("HH:mm") : ""}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-800">
                        <div>{row.customer || "-"}</div>
                        <div className="text-[11px] text-neutral-500">{row.customer_phone || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-neutral-800 whitespace-nowrap">
                        {branchNameById.get(String(row.outlet_id)) || `Outlet ${row.outlet_id}`}
                      </td>
                      <td className="px-4 py-3 font-semibold text-right text-neutral-900 whitespace-nowrap">
                        {formatCurrency(row.nett_amount)}
                        {Number(row.additional_discount_amount ?? 0) > 0 && (
                          <div className="text-[11px] font-normal text-neutral-500">
                            disc {formatCurrency(row.additional_discount_amount)}
                            {row.additional_discount_type === "percentage" ? " (%)" : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${statusBadgeClass(rowStatus)}`}>
                          {isCanceled ? "DIBATALKAN" : rowStatus || "-"}
                        </span>
                        {isCanceled && row.cancel_reason && (
                          <div className="text-[11px] text-neutral-500 mt-1 max-w-[180px] truncate" title={row.cancel_reason}>
                            {row.cancel_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openDetail(row)}
                            disabled={detailLoadingId === row.id}
                            className={BTN_OUTLINE}
                          >
                            {detailLoadingId === row.id ? "..." : "Detail"}
                          </button>
                          {/* 280726: DO / Surat Jalan / Faktur digabung ke dropdown Print. */}
                          <PrintActions orderId={row.id} onOpen={openPrint} />
                          {rowStatus === "SUBMITTED" && (
                            <>
                              <button
                                type="button"
                                disabled={actingId === row.id}
                                onClick={() => onApprove(row)}
                                className={BTN_GREEN}
                              >
                                {actingId === row.id ? "..." : "Approve"}
                              </button>
                              <button
                                type="button"
                                disabled={actingId === row.id}
                                onClick={() => onReject(row)}
                                className={BTN_OUTLINE_ROSE}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {/* 230726 (permintaan user): order REJECTED bisa diedit ulang —
                              detail diperbaiki lalu otomatis kembali SUBMITTED. */}
                          {rowStatus === "REJECTED" && (
                            <Link
                              to={`/ho-order/${row.id}/edit`}
                              state={{ transition: "fade" }}
                              title="Edit order"
                              className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90"
                            >
                              <Pencil className="w-6 h-6" />
                            </Link>
                          )}
                          {rowStatus === "APPROVED" && (
                            <button
                              type="button"
                              disabled={actingId === row.id}
                              onClick={() => onPosting(row)}
                              className={BTN_BLUE}
                            >
                              {actingId === row.id ? "..." : "Posting"}
                            </button>
                          )}
                          {(rowStatus === "APPROVED" || rowStatus === "DONE" || rowStatus === "PAID") && (
                            <button
                              type="button"
                              disabled={isCanceling}
                              onClick={() => onCancel(row)}
                              className={BTN_ROSE}
                            >
                              {isCanceling ? "..." : "Batalkan"}
                            </button>
                          )}
                          {/* 270726 (permintaan user): hapus order HO — tersedia di semua
                              status; stok dikembalikan dulu bila order belum dibatalkan. */}
                          <button
                            type="button"
                            disabled={deletingId === row.id}
                            onClick={() => onDelete(row)}
                            className={BTN_OUTLINE_DANGER}
                          >
                            {deletingId === row.id ? "..." : "Hapus"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-4 border-t border-neutral-200">
            <Pagination
              links={paginationLinks}
              from={paginator?.from}
              to={paginator?.to}
              total={paginator?.total}
            />
          </div>
        </div>

        {detailOrder && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
            onClick={() => setDetailOrder(null)}
          >
            <div
              className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
                <div>
                  <div className="text-sm font-semibold text-neutral-900">Detail Order {detailOrder.id}</div>
                  <div className="text-[11px] text-neutral-500">
                    {detailOrder.customer || "-"} · {detailOrder.order_date ? moment(detailOrder.order_date).format("DD/MM/YYYY") : "-"} · {String(detailOrder.payment_status || "").toUpperCase()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailOrder(null)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-brand-900 rounded-lg hover:bg-brand-800"
                >
                  Tutup
                </button>
              </div>
              <div className="p-4 overflow-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b">
                      <th className="py-2 pr-2">Produk</th>
                      <th className="py-2 pr-2 w-20 text-right">Qty</th>
                      <th className="py-2 pr-2 w-32 text-right">Harga</th>
                      <th className="py-2 w-32 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailOrder.items || []).map((item, idx) => (
                      <tr key={`${item.product_unit_id}-${idx}`} className="border-b border-neutral-100">
                        <td className="py-2 pr-2">
                          <div className="text-neutral-800">{item.product_unit || item.product_unit_id}</div>
                          <div className="text-[11px] text-neutral-400">{item.product_unit_id}</div>
                        </td>
                        <td className="py-2 pr-2 text-right">{formatQty(item.quantity)}</td>
                        <td className="py-2 pr-2 text-right">{formatCurrency(item.price)}</td>
                        <td className="py-2 text-right">{formatCurrency(item.total_nett ?? item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4 space-y-1 text-sm max-w-xs ml-auto">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Subtotal</span>
                    <span>{formatCurrency(detailOrder.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Diskon Order</span>
                    <span>- {formatCurrency(detailOrder.additional_discount_amount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-1">
                    <span>Total (Nett)</span>
                    <span className="text-orange-500">{formatCurrency(detailOrder.nett_amount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Transition>
  )
}
